import React, { ElementType } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { LayoutGrid, Rows3, Search } from 'lucide-react';

export const quoteAccent = '#0E766D';
export const quoteAccentDark = '#0B5E57';
export const quoteBorder = 'gray.200';
export const quoteSurface = 'white';
export const quoteSurfaceMuted = 'gray.50';

export const formatQuoteMoney = (amount?: number | null) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);

export const formatQuoteDate = (value?: string | null) => {
  if (!value) {
    return 'No date set';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'No date set';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const getQuoteStatusMeta = (status?: string) => {
  switch (status) {
    case 'sent':
      return { label: 'Sent', colorScheme: 'blue' as const };
    case 'accepted':
      return { label: 'Accepted', colorScheme: 'green' as const };
    case 'rejected':
      return { label: 'Rejected', colorScheme: 'red' as const };
    case 'expired':
      return { label: 'Expired', colorScheme: 'orange' as const };
    case 'draft':
    default:
      return { label: 'Draft', colorScheme: 'gray' as const };
  }
};

export const QuoteShell = ({
  children,
  compact = false,
}: {
  children: React.ReactNode;
  compact?: boolean;
}) => (
  <Box
    px={{ base: 4, md: 6, xl: 8 }}
    py={{ base: 4, md: 6 }}
    bg="linear-gradient(180deg, #f3fbfa 0%, #ffffff 34%)"
    minH="calc(100vh - 72px)"
  >
    <Box maxW={compact ? '1120px' : '1360px'} mx="auto">
      {children}
    </Box>
  </Box>
);

export const QuoteHero = ({
  eyebrow = 'Quotations',
  title,
  description,
  primaryAction,
  secondaryAction,
  stats,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  stats?: Array<{ label: string; value: string }>;
}) => (
  <Box
    bg={quoteSurface}
    borderWidth="1px"
    borderColor={quoteBorder}
    borderRadius={{ base: '24px', md: '28px' }}
    px={{ base: 5, md: 7 }}
    py={{ base: 5, md: 7 }}
    boxShadow="0 18px 48px rgba(15, 23, 42, 0.06)"
    mb={6}
  >
    <Flex
      align={{ base: 'stretch', lg: 'flex-start' }}
      justify="space-between"
      gap={6}
      direction={{ base: 'column', lg: 'row' }}
    >
      <VStack align="flex-start" spacing={3} maxW="3xl">
        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="0.18em"
          fontWeight="700"
          color={quoteAccent}
        >
          {eyebrow}
        </Text>
        <Text
          as="h1"
          fontSize={{ base: '2xl', md: '4xl' }}
          fontWeight="800"
          lineHeight={{ base: '1.2', md: '1.05' }}
          color="gray.900"
          letterSpacing="-0.03em"
        >
          {title}
        </Text>
        <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600" maxW="2xl">
          {description}
        </Text>
        {stats?.length ? (
          <SimpleGrid
            columns={{ base: 2, md: Math.min(stats.length, 4) }}
            spacing={3}
            w="full"
            pt={2}
          >
            {stats.map((stat) => (
              <Box
                key={stat.label}
                borderWidth="1px"
                borderColor="gray.100"
                bg={quoteSurfaceMuted}
                borderRadius="18px"
                px={4}
                py={3}
              >
                <Text fontSize="xs" color="gray.500" mb={1}>
                  {stat.label}
                </Text>
                <Text fontSize="lg" fontWeight="700" color="gray.900">
                  {stat.value}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        ) : null}
      </VStack>
      {(primaryAction || secondaryAction) && (
        <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
          {primaryAction}
          {secondaryAction}
        </Stack>
      )}
    </Flex>
  </Box>
);

export const QuotePrimaryButton = ({
  children,
  ...props
}: React.ComponentProps<typeof Button>) => (
  <Button
    bg={quoteAccent}
    color="white"
    _hover={{ bg: quoteAccentDark, transform: 'translateY(-1px)' }}
    _active={{ bg: quoteAccentDark }}
    boxShadow="0 12px 26px rgba(14, 118, 109, 0.2)"
    borderRadius="16px"
    fontWeight="700"
    px={5}
    {...props}
  >
    {children}
  </Button>
);

export const QuoteSectionCard = ({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    bg={quoteSurface}
    borderWidth="1px"
    borderColor={quoteBorder}
    borderRadius="24px"
    px={{ base: 4, md: 6 }}
    py={{ base: 4, md: 5 }}
    boxShadow="0 12px 32px rgba(15, 23, 42, 0.04)"
  >
    <Flex
      justify="space-between"
      align={{ base: 'flex-start', md: 'center' }}
      direction={{ base: 'column', md: 'row' }}
      gap={3}
      mb={5}
    >
      <Box>
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" color="gray.900">
          {title}
        </Text>
        {description ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {description}
          </Text>
        ) : null}
      </Box>
      {action}
    </Flex>
    {children}
  </Box>
);

export const QuoteStatusBadge = ({ status }: { status?: string }) => {
  const meta = getQuoteStatusMeta(status);
  return (
    <Badge borderRadius="full" px={3} py={1} colorScheme={meta.colorScheme}>
      {meta.label}
    </Badge>
  );
};

export const QuoteField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text color="gray.900" fontWeight="600">
      {value}
    </Text>
  </Box>
);

export const QuoteCommandBar = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  sort,
  onSortChange,
  view,
  onViewChange,
  resultText,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  sort: string;
  onSortChange: (value: string) => void;
  view: 'table' | 'cards';
  onViewChange: (value: 'table' | 'cards') => void;
  resultText: string;
}) => (
  <Stack
    spacing={4}
    bg="rgba(255,255,255,0.9)"
    borderWidth="1px"
    borderColor={quoteBorder}
    borderRadius="24px"
    p={{ base: 4, md: 5 }}
    mb={5}
    position="sticky"
    top={{ base: '72px', md: '84px' }}
    zIndex={5}
    backdropFilter="blur(14px)"
  >
    <Flex
      direction={{ base: 'column', xl: 'row' }}
      justify="space-between"
      align={{ base: 'stretch', xl: 'center' }}
      gap={3}
    >
      <InputGroup maxW={{ xl: '440px' }}>
        <InputLeftElement pointerEvents="none">
          <Search size={16} color="#718096" />
        </InputLeftElement>
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search by quote number, customer, company, or amount"
          borderRadius="16px"
          bg={quoteSurfaceMuted}
          borderColor="gray.200"
          _focusVisible={{
            borderColor: 'teal.300',
            boxShadow: '0 0 0 3px rgba(45, 212, 191, 0.18)',
            bg: 'white',
          }}
        />
      </InputGroup>

      <Flex gap={3} wrap="wrap" justify={{ base: 'stretch', xl: 'flex-end' }}>
        <Select
          value={status}
          onChange={(event) => onStatusChange(event.target.value)}
          borderRadius="16px"
          bg={quoteSurfaceMuted}
          maxW={{ base: 'full', md: '190px' }}
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
        </Select>

        <Select
          value={sort}
          onChange={(event) => onSortChange(event.target.value)}
          borderRadius="16px"
          bg={quoteSurfaceMuted}
          maxW={{ base: 'full', md: '200px' }}
        >
          <option value="created-desc">Newest first</option>
          <option value="created-asc">Oldest first</option>
          <option value="amount-desc">Highest total</option>
          <option value="amount-asc">Lowest total</option>
        </Select>

        <HStack spacing={1} borderWidth="1px" borderColor="gray.200" borderRadius="16px" p={1}>
          <IconButton
            aria-label="Table view"
            icon={<Box as={Rows3 as ElementType} />}
            size="sm"
            variant={view === 'table' ? 'solid' : 'ghost'}
            bg={view === 'table' ? quoteAccent : 'transparent'}
            color={view === 'table' ? 'white' : 'gray.600'}
            _hover={view === 'table' ? { bg: quoteAccent } : { bg: 'gray.100' }}
            onClick={() => onViewChange('table')}
          />
          <IconButton
            aria-label="Card view"
            icon={<Box as={LayoutGrid as ElementType} />}
            size="sm"
            variant={view === 'cards' ? 'solid' : 'ghost'}
            bg={view === 'cards' ? quoteAccent : 'transparent'}
            color={view === 'cards' ? 'white' : 'gray.600'}
            _hover={view === 'cards' ? { bg: quoteAccent } : { bg: 'gray.100' }}
            onClick={() => onViewChange('cards')}
          />
        </HStack>
      </Flex>
    </Flex>
    <Text fontSize="sm" color="gray.500">
      {resultText}
    </Text>
  </Stack>
);

export const QuoteEmptyState = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <VStack
    bg={quoteSurface}
    borderWidth="1px"
    borderColor={quoteBorder}
    borderRadius="28px"
    px={{ base: 6, md: 10 }}
    py={{ base: 10, md: 14 }}
    spacing={4}
    textAlign="center"
    boxShadow="0 12px 32px rgba(15, 23, 42, 0.04)"
  >
    <Box
      w="64px"
      h="64px"
      borderRadius="20px"
      bg="rgba(14, 118, 109, 0.10)"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box as={Rows3 as ElementType} boxSize={7} color={quoteAccent} />
    </Box>
    <VStack spacing={2}>
      <Text fontSize="xl" fontWeight="800" color="gray.900">
        {title}
      </Text>
      <Text fontSize="sm" color="gray.500" maxW="md">
        {description}
      </Text>
    </VStack>
    {action}
  </VStack>
);

export const QuoteDashboardSkeleton = () => (
  <Stack spacing={4}>
    {[...Array(5)].map((_, idx) => (
      <Box
        key={idx}
        bg={quoteSurface}
        borderWidth="1px"
        borderColor={quoteBorder}
        borderRadius="22px"
        px={5}
        py={4}
      >
        <HStack spacing={4} align="flex-start">
          <Box flex="1">
            <Skeleton h="18px" w="30%" mb={3} />
            <SkeletonText noOfLines={2} spacing="3" />
          </Box>
          <Skeleton h="36px" w="90px" borderRadius="full" />
        </HStack>
      </Box>
    ))}
  </Stack>
);
